import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Alert, Badge } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import QRCodeStyling from 'qr-code-styling';
import {
  BsQrCode, BsGlobe, BsWifi, BsPersonVcard, BsChatLeftText,
  BsEnvelope, BsChatDots, BsTelephone, BsCheckCircleFill,
  BsDownload, BsClipboard, BsEye, BsEyeSlash,
  BsShieldCheck, BsUpload
} from 'react-icons/bs';
import { FaFrog } from 'react-icons/fa';
import PEPE_BASE64 from '../assets/pepeBase64';
import '../css/DevQRCode.css';

// --- Wi-Fi 특수문자 이스케이프 헬퍼 ---
const escapeWifi = (str = '') => {
  return str.replace(/([\\;,:"])/g, '\\$1');
};

// --- UTF-8 바이트 시퀀스 변환 헬퍼 (스마트폰 한글/유니코드 소실 및 깨짐 완벽 방지) ---
const toUtf8BinaryString = (str = '') => {
  if (!str) return '';
  try {
    return unescape(encodeURIComponent(str));
  } catch (e) {
    return str;
  }
};

// --- 인기 테마 프리셋 ---
const THEME_PRESETS = [
  {
    id: 'pepe',
    name: '🐸 페페 에메랄드',
    dotType: 'rounded',
    dotColor1: '#059669',
    dotColor2: '#10b981',
    isGradient: true,
    cornerSquareType: 'extra-rounded',
    cornerSquareColor: '#047857',
    cornerDotType: 'dot',
    cornerDotColor: '#065f46',
    bgColor: '#ffffff',
    frameColor: '#059669',
    frameTextColor: '#ffffff'
  },
  {
    id: 'electric-blue',
    name: '⚡ 일렉트릭 블루',
    dotType: 'classy-rounded',
    dotColor1: '#1d59f9',
    dotColor2: '#3b82f6',
    isGradient: true,
    cornerSquareType: 'extra-rounded',
    cornerSquareColor: '#1d59f9',
    cornerDotType: 'dot',
    cornerDotColor: '#1e40af',
    bgColor: '#ffffff',
    frameColor: '#1d59f9',
    frameTextColor: '#ffffff'
  },
  {
    id: 'midnight',
    name: '🌑 미드나잇 다크',
    dotType: 'square',
    dotColor1: '#0f172a',
    dotColor2: '#334155',
    isGradient: false,
    cornerSquareType: 'square',
    cornerSquareColor: '#0f172a',
    cornerDotType: 'square',
    cornerDotColor: '#0f172a',
    bgColor: '#ffffff',
    frameColor: '#0f172a',
    frameTextColor: '#ffffff'
  },
  {
    id: 'neon',
    name: '🟣 네온 사이버',
    dotType: 'dots',
    dotColor1: '#7c3aed',
    dotColor2: '#ec4899',
    isGradient: true,
    cornerSquareType: 'dot',
    cornerSquareColor: '#7c3aed',
    cornerDotType: 'dot',
    cornerDotColor: '#ec4899',
    bgColor: '#ffffff',
    frameColor: '#7c3aed',
    frameTextColor: '#ffffff'
  },
  {
    id: 'sunset',
    name: '🌅 선셋 코랄',
    dotType: 'rounded',
    dotColor1: '#ea580c',
    dotColor2: '#f59e0b',
    isGradient: true,
    cornerSquareType: 'extra-rounded',
    cornerSquareColor: '#ea580c',
    cornerDotType: 'dot',
    cornerDotColor: '#c2410c',
    bgColor: '#ffffff',
    frameColor: '#ea580c',
    frameTextColor: '#ffffff'
  }
];

export function DevQRCode() {
  // 1. 활성화된 탭
  const [activeTab, setActiveTab] = useState('content'); // 'content' | 'frame' | 'shape' | 'color' | 'pepe'
  const [contentType, setContentType] = useState('url'); // 'url' | 'wifi' | 'vcard' | 'text' | 'email' | 'sms' | 'phone'

  // 2. 콘텐츠 데이터 상태 (기본 주소: https://www.wajok.com)
  const [urlData, setUrlData] = useState('https://www.wajok.com');
  const [wifiData, setWifiData] = useState({
    ssid: 'Wajok_Office_5G',
    password: 'pepepassword1234',
    security: 'WPA', // 'WPA' | 'WEP' | 'nopass'
    hidden: false
  });
  const [showWifiPassword, setShowWifiPassword] = useState(false);
  const [vcardData, setVcardData] = useState({
    firstName: '와족',
    lastName: '김',
    phone: '010-1234-5678',
    email: 'wajok@wajok.com',
    company: '와족 스튜디오',
    title: '풀스택 개발자',
    website: 'https://www.wajok.com'
  });
  const [textData, setTextData] = useState('와! 페페가 강력 추천하는 QR 코드입니다.');
  const [vcardFormat, setVcardFormat] = useState('vcard3'); // 'vcard3' | 'mecard'
  const [emailData, setEmailData] = useState({
    email: 'contact@wajok.com',
    subject: '[와족닷컴] 문의드립니다',
    body: '안녕하세요! QR코드를 통해 연락드립니다.'
  });
  const [smsData, setSmsData] = useState({
    phone: '010-1234-5678',
    message: '안녕하세요! 와족닷컴 QR코드 메시지입니다.'
  });
  const [phoneData, setPhoneData] = useState('010-1234-5678');

  // 3. 프레임
  const [frameStyle, setFrameStyle] = useState('bottom-banner'); // 'none' | 'bottom-banner' | 'top-banner' | 'modern-card' | 'speech-bubble'
  const [frameText, setFrameText] = useState('SCAN ME');
  const [frameColor, setFrameColor] = useState('#059669');
  const [frameTextColor, setFrameTextColor] = useState('#ffffff');

  // 4. 형태 & 도트 디자인
  const [dotType, setDotType] = useState('rounded'); // 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'square' | 'extra-rounded'
  const [cornerSquareType, setCornerSquareType] = useState('extra-rounded'); // 'square' | 'extra-rounded' | 'dot'
  const [cornerDotType, setCornerDotType] = useState('dot'); // 'square' | 'dot'

  // 5. 색상 & 그라데이션
  const [isGradient, setIsGradient] = useState(true);
  const [dotColor1, setDotColor1] = useState('#059669');
  const [dotColor2, setDotColor2] = useState('#10b981');
  const [cornerSquareColor, setCornerSquareColor] = useState('#047857');
  const [cornerDotColor, setCornerDotColor] = useState('#065f46');
  const [bgColor, setBgColor] = useState('#ffffff');

  // 6. 페페 로고 & 이미지 (핵심 기능)
  const [enableLogo, setEnableLogo] = useState(true);
  const [logoImage, setLogoImage] = useState(PEPE_BASE64);
  const [logoSize, setLogoSize] = useState(0.24); // 0.15 ~ 0.35 (에러 복원율 H 기준 최적 0.24)
  const [logoMargin, setLogoMargin] = useState(4);
  const [hideBackgroundDots, setHideBackgroundDots] = useState(true);

  // 7. 반응형 미리보기 크기 (모바일 200px, 데스크탑 280px)
  const [previewSize, setPreviewSize] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768 ? 200 : 280;
  });

  useEffect(() => {
    const handleResize = () => {
      setPreviewSize(window.innerWidth < 768 ? 200 : 280);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 8. 해상도 및 내보내기 상태
  const [selectedResolution, setSelectedResolution] = useState(512); // 256, 512, 1024, 2048, 4096
  const [isCustomRes, setIsCustomRes] = useState(false);
  const [customResValue, setCustomResValue] = useState(1024);
  const [isDownloading, setIsDownloading] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  // DOM 캔버스 컨테이너 Ref
  const qrContainerRef = useRef(null);
  const qrCodeInstanceRef = useRef(null);
  const fileInputRef = useRef(null);

  // 토스트 타이머
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // 최종 인코딩 데이터 계산
  const encodedData = useMemo(() => {
    switch (contentType) {
      case 'url': {
        let u = urlData.trim();
        if (!u) return 'https://www.wajok.com';
        if (!/^https?:\/\//i.test(u) && !u.startsWith('/')) {
          u = 'https://' + u;
        }
        return u;
      }
      case 'wifi': {
        const { ssid, password, security, hidden } = wifiData;
        if (security === 'nopass') {
          return `WIFI:T:nopass;S:${escapeWifi(ssid)};H:${hidden ? 'true' : 'false'};;`;
        }
        return `WIFI:T:${security};S:${escapeWifi(ssid)};P:${escapeWifi(password)};H:${hidden ? 'true' : 'false'};;`;
      }
      case 'vcard': {
        const cleanLast = (vcardData.lastName || '').trim();
        const cleanFirst = (vcardData.firstName || '').trim();
        const cleanFull = `${cleanLast}${cleanFirst}`.trim() || `${cleanFirst} ${cleanLast}`.trim() || '연락처';
        const cleanPhone = (vcardData.phone || '').trim();
        const cleanEmail = (vcardData.email || '').trim();
        const cleanCompany = (vcardData.company || '').trim();
        const cleanTitle = (vcardData.title || '').trim();
        const cleanWebsite = (vcardData.website || '').trim();

        if (vcardFormat === 'mecard') {
          // MECARD 형식 (안드로이드 간편 명함 규격)
          let me = `MECARD:N:${cleanLast},${cleanFirst};`;
          if (cleanPhone) me += `TEL:${cleanPhone};`;
          if (cleanEmail) me += `EMAIL:${cleanEmail};`;
          if (cleanCompany) me += `ORG:${cleanCompany};`;
          if (cleanTitle) me += `TIL:${cleanTitle};`;
          if (cleanWebsite) me += `URL:${cleanWebsite};`;
          me += ';';
          return me;
        }

        // RFC 2426 vCard 3.0 규격 준수:
        // 1. 반드시 CRLF (\r\n) 개행 구분자 필수 (삼성 갤럭시는 LF 단독 사용 시 빈 연락처로 인식)
        // 2. N (Last;First;;;) 및 FN (Full Name) 필수
        const lines = [
          'BEGIN:VCARD',
          'VERSION:3.0',
          `N:${cleanLast};${cleanFirst};;;`,
          `FN:${cleanFull}`
        ];
        if (cleanPhone) lines.push(`TEL;TYPE=CELL,VOICE:${cleanPhone}`);
        if (cleanCompany) lines.push(`ORG:${cleanCompany}`);
        if (cleanTitle) lines.push(`TITLE:${cleanTitle}`);
        if (cleanEmail) lines.push(`EMAIL;TYPE=INTERNET:${cleanEmail}`);
        if (cleanWebsite) lines.push(`URL:${cleanWebsite}`);
        lines.push('END:VCARD');

        return lines.join('\r\n');
      }
      case 'text':
        return textData || ' ';
      case 'email': {
        const { email, subject, body } = emailData;
        return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      }
      case 'sms': {
        const cleanPhone = (smsData.phone || '').trim();
        const cleanMessage = smsData.message || '';
        // 갤럭시 및 표준 안드로이드 카메라 호환 규격: SMSTO:전화번호:메시지내용
        return `SMSTO:${cleanPhone}:${cleanMessage}`;
      }
      case 'phone':
        return `tel:${phoneData}`;
      default:
        return urlData;
    }
  }, [contentType, urlData, wifiData, vcardData, vcardFormat, textData, emailData, smsData, phoneData]);

  // QR Code 옵션 객체 생성
  const getQROptions = useCallback((size = 280) => {
    const dotsOpts = {
      type: dotType,
      color: dotColor1
    };
    if (isGradient) {
      dotsOpts.gradient = {
        type: 'linear',
        rotation: 0.785, // 45도
        colorStops: [
          { offset: 0, color: dotColor1 },
          { offset: 1, color: dotColor2 }
        ]
      };
    }

    // 한글 및 유니코드 문자가 깨지지 않도록 UTF-8 바이트 시퀀스로 인코딩하여 라이브러리에 전달
    const utf8Data = toUtf8BinaryString(encodedData);

    return {
      width: size,
      height: size,
      type: 'canvas',
      data: utf8Data,
      image: enableLogo ? logoImage : undefined,
      margin: 4,
      qrOptions: {
        typeNumber: 0,
        mode: 'Byte',
        errorCorrectionLevel: 'H' // 페페 이미지 강제 임베딩 시 완벽 복원 보장!
      },
      imageOptions: {
        hideBackgroundDots: hideBackgroundDots,
        imageSize: logoSize,
        margin: logoMargin,
        crossOrigin: 'anonymous',
        saveAsBlob: true
      },
      dotsOptions: dotsOpts,
      backgroundOptions: {
        color: bgColor
      },
      cornersSquareOptions: {
        type: cornerSquareType,
        color: cornerSquareColor
      },
      cornersDotOptions: {
        type: cornerDotType,
        color: cornerDotColor
      }
    };
  }, [
    encodedData, enableLogo, logoImage, logoSize, logoMargin, hideBackgroundDots,
    dotType, isGradient, dotColor1, dotColor2, bgColor,
    cornerSquareType, cornerSquareColor, cornerDotType, cornerDotColor
  ]);

  // 실시간 QR 코드 인스턴스 렌더링 & 업데이트
  useEffect(() => {
    const options = getQROptions(previewSize);

    if (!qrCodeInstanceRef.current) {
      qrCodeInstanceRef.current = new QRCodeStyling(options);
      if (qrContainerRef.current) {
        qrContainerRef.current.innerHTML = '';
        qrCodeInstanceRef.current.append(qrContainerRef.current);
      }
    } else {
      qrCodeInstanceRef.current.update(options);
    }
  }, [getQROptions, previewSize]);

  // 프리셋 테마 적용
  const handleApplyTheme = (theme) => {
    setDotType(theme.dotType);
    setDotColor1(theme.dotColor1);
    setDotColor2(theme.dotColor2);
    setIsGradient(theme.isGradient);
    setCornerSquareType(theme.cornerSquareType);
    setCornerSquareColor(theme.cornerSquareColor);
    setCornerDotType(theme.cornerDotType);
    setCornerDotColor(theme.cornerDotColor);
    setBgColor(theme.bgColor);
    setFrameColor(theme.frameColor);
    setFrameTextColor(theme.frameTextColor);
  };

  // 사용자 정의 로고 업로드 핸들러
  const handleCustomLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setLogoImage(reader.result);
      setEnableLogo(true);
      setToastMessage('새로운 로고가 적용되었습니다!');
    };
    reader.readAsDataURL(file);
  };

  // 페페 로고로 원복
  const handleResetToPepe = () => {
    setLogoImage(PEPE_BASE64);
    setEnableLogo(true);
    setLogoSize(0.24);
    setToastMessage('🐸 원조 페페 로고로 복원되었습니다!');
  };

  // 최종 유효 해상도
  const targetResolution = isCustomRes ? Math.max(128, Math.min(4096, customResValue || 1024)) : selectedResolution;

  // --- 고화질 PNG 다운로드 (프레임 합성 포함) ---
  const handleDownloadPNG = async () => {
    setIsDownloading(true);
    try {
      const res = targetResolution;
      const tempQR = new QRCodeStyling({
        ...getQROptions(res),
        width: res,
        height: res,
        type: 'canvas'
      });
      const qrBlob = await tempQR.getRawData('png');
      const qrBitmap = await createImageBitmap(qrBlob);

      let finalCanvas = document.createElement('canvas');
      let ctx = finalCanvas.getContext('2d');

      if (frameStyle === 'none') {
        finalCanvas.width = res;
        finalCanvas.height = res;
        ctx.drawImage(qrBitmap, 0, 0, res, res);
      } else {
        const padding = Math.round(res * 0.08);
        const bannerHeight = Math.round(res * 0.18);
        const totalWidth = res + padding * 2;
        const totalHeight = res + bannerHeight + padding * 2 + 10;

        finalCanvas.width = totalWidth;
        finalCanvas.height = totalHeight;

        ctx.fillStyle = '#ffffff';
        const radius = Math.round(res * 0.05);
        ctx.beginPath();
        ctx.roundRect(0, 0, totalWidth, totalHeight, radius);
        ctx.fill();

        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = Math.max(2, Math.round(res * 0.005));
        ctx.stroke();

        let qrY = padding;
        let bannerY = padding + res + 10;

        if (frameStyle === 'top-banner') {
          bannerY = padding;
          qrY = padding + bannerHeight + 10;
        }

        ctx.drawImage(qrBitmap, padding, qrY, res, res);

        const bannerPaddingX = Math.round(padding * 0.5);
        const bannerWidth = totalWidth - bannerPaddingX * 2;
        const bannerRadius = Math.round(bannerHeight * 0.3);

        ctx.fillStyle = frameColor;
        ctx.beginPath();
        ctx.roundRect(bannerPaddingX, bannerY, bannerWidth, bannerHeight, bannerRadius);
        ctx.fill();

        ctx.fillStyle = frameTextColor;
        ctx.font = `bold ${Math.round(bannerHeight * 0.45)}px 'Pretendard', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(frameText, totalWidth / 2, bannerY + bannerHeight / 2);
      }

      const link = document.createElement('a');
      link.download = `qrcode_${contentType}_pepe_${res}px.png`;
      link.href = finalCanvas.toDataURL('image/png');
      link.click();

      setToastMessage(`🎉 ${res}px 고화질 PNG가 다운로드되었습니다!`);
    } catch (err) {
      console.error(err);
      setToastMessage('다운로드 중 오류가 발생했습니다.');
    } finally {
      setIsDownloading(false);
    }
  };

  // --- SVG 무손실 벡터 다운로드 ---
  const handleDownloadSVG = async () => {
    setIsDownloading(true);
    try {
      const res = targetResolution;
      const tempQR = new QRCodeStyling({
        ...getQROptions(res),
        width: res,
        height: res,
        type: 'svg'
      });
      const svgBlob = await tempQR.getRawData('svg');
      const svgText = await svgBlob.text();

      let finalSvgString = svgText;

      if (frameStyle !== 'none') {
        const padding = Math.round(res * 0.08);
        const bannerHeight = Math.round(res * 0.18);
        const totalWidth = res + padding * 2;
        const totalHeight = res + bannerHeight + padding * 2 + 10;

        let qrY = padding;
        let bannerY = padding + res + 10;
        if (frameStyle === 'top-banner') {
          bannerY = padding;
          qrY = padding + bannerHeight + 10;
        }

        const bannerRadius = Math.round(bannerHeight * 0.3);
        const bannerPaddingX = Math.round(padding * 0.5);
        const bannerWidth = totalWidth - bannerPaddingX * 2;
        const fontSize = Math.round(bannerHeight * 0.45);

        const innerSvgContent = svgText
          .replace(/<\?xml.*?\?>/i, '')
          .replace(/<svg[^>]*>/i, '')
          .replace(/<\/svg>/i, '');

        finalSvgString = `<?xml version="1.0" standalone="no"?>
<svg width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}" xmlns="http://www.w3.org/2000/svg">
  <rect width="100%" height="100%" fill="#ffffff" rx="${Math.round(res * 0.05)}"/>
  <g transform="translate(${padding}, ${qrY})">
    ${innerSvgContent}
  </g>
  <rect x="${bannerPaddingX}" y="${bannerY}" width="${bannerWidth}" height="${bannerHeight}" rx="${bannerRadius}" fill="${frameColor}"/>
  <text x="${totalWidth / 2}" y="${bannerY + bannerHeight / 2 + fontSize * 0.35}" fill="${frameTextColor}" font-family="'Pretendard', sans-serif" font-weight="bold" font-size="${fontSize}" text-anchor="middle">
    ${frameText}
  </text>
</svg>`;
      }

      const blob = new Blob([finalSvgString], { type: 'image/svg+xml;charset=utf-8' });
      const link = document.createElement('a');
      link.download = `qrcode_${contentType}_pepe_${res}px.svg`;
      link.href = URL.createObjectURL(blob);
      link.click();

      setToastMessage('🎉 무손실 SVG 벡터 파일이 다운로드되었습니다!');
    } catch (err) {
      console.error(err);
      setToastMessage('SVG 다운로드 중 오류가 발생했습니다.');
    } finally {
      setIsDownloading(false);
    }
  };

  // --- 클립보드에 이미지 복사 ---
  const handleCopyToClipboard = async () => {
    try {
      const tempQR = new QRCodeStyling({
        ...getQROptions(512),
        width: 512,
        height: 512,
        type: 'canvas'
      });
      const blob = await tempQR.getRawData('png');
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      setToastMessage('📋 QR 이미지가 클립보드에 복사되었습니다!');
    } catch (err) {
      console.warn(err);
      setToastMessage('클립보드 복사를 지원하지 않는 브라우저입니다.');
    }
  };

  return (
    <div className="qr-studio-container">
      <Container fluid="lg">
        {/* 상단 헤더 */}
        <div className="qr-header-section">
          <h1 className="qr-main-title">QR코드 공작소</h1>
        </div>

        {/* 토스트 알림 */}
        <AnimatePresence>
          {toastMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{
                position: 'fixed',
                top: 24,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 9999
              }}
            >
              <Alert variant="success" className="shadow-lg py-2 px-4 rounded-pill fw-bold d-flex align-items-center gap-2">
                <BsCheckCircleFill /> {toastMessage}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 메인 레이아웃: 모바일에서는 프리뷰가 상단(order-1), 데스크탑에서는 프리뷰가 우측(order-lg-2) */}
        <Row className="g-3 g-lg-4">
          {/* 실시간 폰 프리뷰 & 다운로드 영역 (모바일 상단 배치로 실시간 확인 극대화) */}
          <Col lg={5} className="order-1 order-lg-2">
            <div className="qr-preview-sticky-wrapper">
              <div className="qr-phone-mockup-frame">
                <div className="qr-phone-speaker" />

                {/* 프레임 및 QR 코드 합성 뷰 */}
                <div className="qr-composite-frame mb-3" style={{ background: '#ffffff' }}>
                  {/* 상단 배너 프레임 */}
                  {frameStyle === 'top-banner' && (
                    <div
                      className="qr-frame-banner-tag qr-frame-banner-top"
                      style={{ background: frameColor, color: frameTextColor }}
                    >
                      {frameText}
                    </div>
                  )}

                  {/* QR 코드 실시간 렌더링 캔버스 */}
                  <div ref={qrContainerRef} className="qr-canvas-display-box" />

                  {/* 하단 배너 프레임 */}
                  {frameStyle === 'bottom-banner' && (
                    <div
                      className="qr-frame-banner-tag qr-frame-banner-bottom"
                      style={{ background: frameColor, color: frameTextColor }}
                    >
                      {frameText}
                    </div>
                  )}

                  {/* 모던 카드 테두리 라벨 */}
                  {frameStyle === 'modern-card' && (
                    <div
                      className="mt-2 py-1 px-3 rounded-pill fw-bold text-center"
                      style={{ background: frameColor, color: frameTextColor, fontSize: '0.82rem' }}
                    >
                      {frameText}
                    </div>
                  )}
                </div>

                {/* 해상도 선택기 */}
                <div className="mt-2 text-start">
                  <Form.Label className="fw-bold text-dark small mb-1">
                    다운로드 해상도 (Pixel Size)
                  </Form.Label>
                  <div className="qr-resolution-pill-group">
                    {[256, 512, 1024, 2048, 4096].map(res => (
                      <button
                        key={res}
                        type="button"
                        className={`qr-res-btn ${!isCustomRes && selectedResolution === res ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedResolution(res);
                          setIsCustomRes(false);
                        }}
                      >
                        {res}px
                      </button>
                    ))}
                    <button
                      type="button"
                      className={`qr-res-btn ${isCustomRes ? 'active' : ''}`}
                      onClick={() => setIsCustomRes(true)}
                    >
                      직접입력
                    </button>
                  </div>

                  {isCustomRes && (
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Form.Control
                        type="number"
                        size="sm"
                        min={128}
                        max={4096}
                        step={64}
                        value={customResValue}
                        onChange={(e) => setCustomResValue(parseInt(e.target.value, 10) || 512)}
                        placeholder="px 단위 입력 (예: 800)"
                      />
                      <span className="small text-muted text-nowrap">px 크기</span>
                    </div>
                  )}
                </div>

                {/* 다운로드 버튼 그룹 */}
                <div className="qr-download-btn-group">
                  <button
                    type="button"
                    className="qr-btn-primary-png"
                    onClick={handleDownloadPNG}
                    disabled={isDownloading}
                  >
                    <BsDownload />
                    <span>PNG 다운로드</span>
                  </button>
                  <button
                    type="button"
                    className="qr-btn-secondary-svg"
                    onClick={handleDownloadSVG}
                    disabled={isDownloading}
                  >
                    <BsDownload />
                    <span>SVG 벡터</span>
                  </button>
                </div>

                {/* 클립보드 복사 버튼 */}
                <button
                  type="button"
                  className="qr-btn-copy"
                  onClick={handleCopyToClipboard}
                >
                  <BsClipboard /> 이미지 클립보드 복사
                </button>

                {/* 카메라 스캔 가이드 */}
                <div className="qr-scan-guide-box">
                  <strong>📱 스마트폰 카메라로 바로 스캔해보세요!</strong>
                  <div className="text-truncate mt-1 text-muted" title={encodedData}>
                    인코딩 데이터: {encodedData}
                  </div>
                </div>
              </div>
            </div>
          </Col>

          {/* 좌측(모바일에서는 프리뷰 아래): 제어 패널 */}
          <Col lg={7} className="order-2 order-lg-1">
            <div className="qr-glass-card">
              {/* 콘텐츠 유형 선택 바 (모바일 가로 스와이프 지원) */}
              <div className="qr-type-selector-grid">
                <button
                  type="button"
                  className={`qr-type-btn ${contentType === 'url' ? 'active' : ''}`}
                  onClick={() => setContentType('url')}
                >
                  <BsGlobe className="type-icon" />
                  <span>웹사이트</span>
                </button>
                <button
                  type="button"
                  className={`qr-type-btn ${contentType === 'wifi' ? 'active' : ''}`}
                  onClick={() => setContentType('wifi')}
                >
                  <BsWifi className="type-icon" />
                  <span>와이파이</span>
                </button>
                <button
                  type="button"
                  className={`qr-type-btn ${contentType === 'vcard' ? 'active' : ''}`}
                  onClick={() => setContentType('vcard')}
                >
                  <BsPersonVcard className="type-icon" />
                  <span>명함 연락처</span>
                </button>
                <button
                  type="button"
                  className={`qr-type-btn ${contentType === 'text' ? 'active' : ''}`}
                  onClick={() => setContentType('text')}
                >
                  <BsChatLeftText className="type-icon" />
                  <span>텍스트</span>
                </button>
                <button
                  type="button"
                  className={`qr-type-btn ${contentType === 'email' ? 'active' : ''}`}
                  onClick={() => setContentType('email')}
                >
                  <BsEnvelope className="type-icon" />
                  <span>이메일</span>
                </button>
                <button
                  type="button"
                  className={`qr-type-btn ${contentType === 'sms' ? 'active' : ''}`}
                  onClick={() => setContentType('sms')}
                >
                  <BsChatDots className="type-icon" />
                  <span>SMS</span>
                </button>
                <button
                  type="button"
                  className={`qr-type-btn ${contentType === 'phone' ? 'active' : ''}`}
                  onClick={() => setContentType('phone')}
                >
                  <BsTelephone className="type-icon" />
                  <span>전화번호</span>
                </button>
              </div>

              {/* 상세 편집 서브 탭 네비게이션 */}
              <div className="qr-custom-tabs-nav">
                <button
                  type="button"
                  className={`qr-custom-tab-item ${activeTab === 'content' ? 'active' : ''}`}
                  onClick={() => setActiveTab('content')}
                >
                  1. 내용 입력
                </button>
                <button
                  type="button"
                  className={`qr-custom-tab-item ${activeTab === 'frame' ? 'active' : ''}`}
                  onClick={() => setActiveTab('frame')}
                >
                  2. 프레임/CTA
                </button>
                <button
                  type="button"
                  className={`qr-custom-tab-item ${activeTab === 'shape' ? 'active' : ''}`}
                  onClick={() => setActiveTab('shape')}
                >
                  3. 형태/도트
                </button>
                <button
                  type="button"
                  className={`qr-custom-tab-item ${activeTab === 'color' ? 'active' : ''}`}
                  onClick={() => setActiveTab('color')}
                >
                  4. 색상
                </button>
                <button
                  type="button"
                  className={`qr-custom-tab-item ${activeTab === 'pepe' ? 'active' : ''}`}
                  onClick={() => setActiveTab('pepe')}
                >
                  5. 로고
                </button>
              </div>

              {/* 탭 1: 내용 입력 */}
              {activeTab === 'content' && (
                <div className="qr-tab-content-panel">
                  {/* URL */}
                  {contentType === 'url' && (
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold text-dark">연결할 웹 주소 (URL)</Form.Label>
                      <Form.Control
                        type="url"
                        size="lg"
                        placeholder="https://example.com"
                        value={urlData}
                        onChange={(e) => setUrlData(e.target.value)}
                        className="rounded-3"
                      />
                      <Form.Text className="text-muted">
                        스마트폰 카메라로 스캔 시 즉시 해당 웹사이트로 접속됩니다.
                      </Form.Text>
                      <div className="d-flex gap-2 mt-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setUrlData('https://www.wajok.com')}
                        >
                          내 블로그 주소 넣기
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={async () => {
                            try {
                              const text = await navigator.clipboard.readText();
                              if (text) setUrlData(text);
                            } catch (e) { }
                          }}
                        >
                          클립보드 주소 붙여넣기
                        </Button>
                      </div>
                    </Form.Group>
                  )}

                  {/* 와이파이 */}
                  {contentType === 'wifi' && (
                    <div>
                      <Alert variant="info" className="d-flex align-items-center gap-2 py-2 mb-3">
                        <BsShieldCheck className="fs-5" />
                        <div>스마트폰 카메라로 스캔하면 <strong>비밀번호 입력 없이 원클릭 접속</strong>됩니다!</div>
                      </Alert>
                      <Row className="g-3 mb-3">
                        <Col md={8}>
                          <Form.Group>
                            <Form.Label className="fw-bold text-dark">네트워크 이름 (SSID)</Form.Label>
                            <Form.Control
                              type="text"
                              value={wifiData.ssid}
                              onChange={(e) => setWifiData({ ...wifiData, ssid: e.target.value })}
                              placeholder="와이파이 이름"
                              className="rounded-3"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label className="fw-bold text-dark">보안 암호화</Form.Label>
                            <Form.Select
                              value={wifiData.security}
                              onChange={(e) => setWifiData({ ...wifiData, security: e.target.value })}
                              className="rounded-3"
                            >
                              <option value="WPA">WPA / WPA2 / WPA3</option>
                              <option value="WEP">WEP</option>
                              <option value="nopass">암호 없음 (Open)</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>

                      {wifiData.security !== 'nopass' && (
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold text-dark">와이파이 비밀번호</Form.Label>
                          <div className="position-relative">
                            <Form.Control
                              type={showWifiPassword ? 'text' : 'password'}
                              value={wifiData.password}
                              onChange={(e) => setWifiData({ ...wifiData, password: e.target.value })}
                              placeholder="Wi-Fi 비밀번호"
                              className="rounded-3 pe-5"
                            />
                            <button
                              type="button"
                              className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-secondary text-decoration-none"
                              onClick={() => setShowWifiPassword(!showWifiPassword)}
                            >
                              {showWifiPassword ? <BsEyeSlash /> : <BsEye />}
                            </button>
                          </div>
                        </Form.Group>
                      )}

                      <Form.Check
                        type="checkbox"
                        id="wifi-hidden"
                        label="비공개 / 숨겨진 네트워크 (Hidden SSID)"
                        checked={wifiData.hidden}
                        onChange={(e) => setWifiData({ ...wifiData, hidden: e.target.checked })}
                      />
                    </div>
                  )}

                  {/* vCard 명함 */}
                  {contentType === 'vcard' && (
                    <div>
                      <Alert variant="secondary" className="py-2 mb-3">
                        스캔 시 스마트폰 연락처에 즉시 명함 정보가 저장됩니다. (갤럭시/아이폰 완벽 호환)
                      </Alert>

                      <div className="d-flex align-items-center justify-content-between mb-3 bg-light p-2 rounded-3 border">
                        <span className="small fw-bold text-dark">명함 규격 선택</span>
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className={`btn ${vcardFormat === 'vcard3' ? 'btn-dark' : 'btn-outline-secondary'}`}
                            onClick={() => setVcardFormat('vcard3')}
                          >
                            vCard 3.0 (표준)
                          </button>
                          <button
                            type="button"
                            className={`btn ${vcardFormat === 'mecard' ? 'btn-dark' : 'btn-outline-secondary'}`}
                            onClick={() => setVcardFormat('mecard')}
                          >
                            MECARD (간편)
                          </button>
                        </div>
                      </div>
                      <Row className="g-2 mb-2">
                        <Col sm={6}>
                          <Form.Label className="fw-semibold small">성 (Last Name)</Form.Label>
                          <Form.Control
                            type="text"
                            value={vcardData.lastName}
                            onChange={(e) => setVcardData({ ...vcardData, lastName: e.target.value })}
                          />
                        </Col>
                        <Col sm={6}>
                          <Form.Label className="fw-semibold small">이름 (First Name)</Form.Label>
                          <Form.Control
                            type="text"
                            value={vcardData.firstName}
                            onChange={(e) => setVcardData({ ...vcardData, firstName: e.target.value })}
                          />
                        </Col>
                      </Row>
                      <Row className="g-2 mb-2">
                        <Col sm={6}>
                          <Form.Label className="fw-semibold small">휴대폰 번호</Form.Label>
                          <Form.Control
                            type="tel"
                            value={vcardData.phone}
                            onChange={(e) => setVcardData({ ...vcardData, phone: e.target.value })}
                          />
                        </Col>
                        <Col sm={6}>
                          <Form.Label className="fw-semibold small">이메일</Form.Label>
                          <Form.Control
                            type="email"
                            value={vcardData.email}
                            onChange={(e) => setVcardData({ ...vcardData, email: e.target.value })}
                          />
                        </Col>
                      </Row>
                      <Row className="g-2">
                        <Col sm={6}>
                          <Form.Label className="fw-semibold small">회사/조직</Form.Label>
                          <Form.Control
                            type="text"
                            value={vcardData.company}
                            onChange={(e) => setVcardData({ ...vcardData, company: e.target.value })}
                          />
                        </Col>
                        <Col sm={6}>
                          <Form.Label className="fw-semibold small">직함/역할</Form.Label>
                          <Form.Control
                            type="text"
                            value={vcardData.title}
                            onChange={(e) => setVcardData({ ...vcardData, title: e.target.value })}
                          />
                        </Col>
                      </Row>
                    </div>
                  )}

                  {/* 텍스트 */}
                  {contentType === 'text' && (
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark">일반 텍스트 / 메모</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={textData}
                        onChange={(e) => setTextData(e.target.value)}
                        placeholder="전달하고 싶은 메시지나 메모를 입력하세요."
                        className="rounded-3"
                      />
                    </Form.Group>
                  )}

                  {/* 이메일 */}
                  {contentType === 'email' && (
                    <div>
                      <Form.Group className="mb-2">
                        <Form.Label className="fw-semibold small">받는 사람 이메일</Form.Label>
                        <Form.Control
                          type="email"
                          value={emailData.email}
                          onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label className="fw-semibold small">메일 제목</Form.Label>
                        <Form.Control
                          type="text"
                          value={emailData.subject}
                          onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="fw-semibold small">메일 본문</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={emailData.body}
                          onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                        />
                      </Form.Group>
                    </div>
                  )}

                  {/* SMS */}
                  {contentType === 'sms' && (
                    <div>
                      <Form.Group className="mb-2">
                        <Form.Label className="fw-semibold small">수신 번호</Form.Label>
                        <Form.Control
                          type="tel"
                          value={smsData.phone}
                          onChange={(e) => setSmsData({ ...smsData, phone: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="fw-semibold small">문자 메시지 내용</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={smsData.message}
                          onChange={(e) => setSmsData({ ...smsData, message: e.target.value })}
                        />
                      </Form.Group>
                    </div>
                  )}

                  {/* 전화번호 */}
                  {contentType === 'phone' && (
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark">연결할 전화번호</Form.Label>
                      <Form.Control
                        type="tel"
                        value={phoneData}
                        onChange={(e) => setPhoneData(e.target.value)}
                        placeholder="010-0000-0000"
                        className="rounded-3"
                      />
                    </Form.Group>
                  )}
                </div>
              )}

              {/* 탭 2: 프레임 & CTA */}
              {activeTab === 'frame' && (
                <div>
                  <Form.Label className="fw-bold text-dark mb-2">프레임 스타일 선택</Form.Label>
                  <div className="qr-frame-grid">
                    <div
                      className={`qr-frame-card ${frameStyle === 'none' ? 'active' : ''}`}
                      onClick={() => setFrameStyle('none')}
                    >
                      <div className="qr-frame-preview-thumb">
                        <BsQrCode className="fs-3 text-secondary" />
                      </div>
                      <span className="qr-frame-card-label">프레임 없음</span>
                    </div>

                    <div
                      className={`qr-frame-card ${frameStyle === 'bottom-banner' ? 'active' : ''}`}
                      onClick={() => setFrameStyle('bottom-banner')}
                    >
                      <div className="qr-frame-preview-thumb justify-content-between p-1">
                        <BsQrCode className="fs-4 text-secondary mt-1" />
                        <div style={{ background: frameColor, height: '10px', width: '100%', borderRadius: '4px' }} />
                      </div>
                      <span className="qr-frame-card-label">하단 배너</span>
                    </div>

                    <div
                      className={`qr-frame-card ${frameStyle === 'top-banner' ? 'active' : ''}`}
                      onClick={() => setFrameStyle('top-banner')}
                    >
                      <div className="qr-frame-preview-thumb justify-content-between p-1">
                        <div style={{ background: frameColor, height: '10px', width: '100%', borderRadius: '4px' }} />
                        <BsQrCode className="fs-4 text-secondary mb-1" />
                      </div>
                      <span className="qr-frame-card-label">상단 배너</span>
                    </div>

                    <div
                      className={`qr-frame-card ${frameStyle === 'modern-card' ? 'active' : ''}`}
                      onClick={() => setFrameStyle('modern-card')}
                    >
                      <div className="qr-frame-preview-thumb p-1 border-2" style={{ borderColor: frameColor }}>
                        <BsQrCode className="fs-4 text-secondary" />
                      </div>
                      <span className="qr-frame-card-label">모던 카드</span>
                    </div>
                  </div>

                  {frameStyle !== 'none' && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold text-dark">프레임 문구 (Call-To-Action)</Form.Label>
                        <Form.Control
                          type="text"
                          value={frameText}
                          onChange={(e) => setFrameText(e.target.value)}
                          placeholder="예: SCAN ME"
                          className="rounded-3"
                        />
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {['SCAN ME', '와이파이 연결', '페페가 보증함', '공식 링크', '연락처 저장'].map(chip => (
                            <Badge
                              key={chip}
                              bg="light"
                              text="dark"
                              className="border p-2 cursor-pointer"
                              style={{ cursor: 'pointer' }}
                              onClick={() => setFrameText(chip)}
                            >
                              {chip}
                            </Badge>
                          ))}
                        </div>
                      </Form.Group>

                      <Row className="g-3">
                        <Col sm={6}>
                          <Form.Label className="fw-bold text-dark">프레임 색상</Form.Label>
                          <div className="d-flex align-items-center gap-2">
                            <Form.Control
                              type="color"
                              value={frameColor}
                              onChange={(e) => setFrameColor(e.target.value)}
                              style={{ width: '48px', height: '38px', padding: '2px' }}
                            />
                            <Form.Control
                              type="text"
                              value={frameColor}
                              onChange={(e) => setFrameColor(e.target.value)}
                            />
                          </div>
                        </Col>
                        <Col sm={6}>
                          <Form.Label className="fw-bold text-dark">글자 색상</Form.Label>
                          <div className="d-flex align-items-center gap-2">
                            <Form.Control
                              type="color"
                              value={frameTextColor}
                              onChange={(e) => setFrameTextColor(e.target.value)}
                              style={{ width: '48px', height: '38px', padding: '2px' }}
                            />
                            <Form.Control
                              type="text"
                              value={frameTextColor}
                              onChange={(e) => setFrameTextColor(e.target.value)}
                            />
                          </div>
                        </Col>
                      </Row>
                    </motion.div>
                  )}
                </div>
              )}

              {/* 탭 3: 형태 & 도트 디자인 */}
              {activeTab === 'shape' && (
                <div>
                  <Form.Label className="fw-bold text-dark mb-2">본체 도트 패턴 (Dots Style)</Form.Label>
                  <div className="qr-shape-grid">
                    {[
                      { id: 'rounded', label: '둥근 사각' },
                      { id: 'dots', label: '원형 도트' },
                      { id: 'classy', label: '클래시' },
                      { id: 'classy-rounded', label: '둥근 클래시' },
                      { id: 'extra-rounded', label: '타원형' },
                      { id: 'square', label: '표준 사각' }
                    ].map(item => (
                      <div
                        key={item.id}
                        className={`qr-shape-item ${dotType === item.id ? 'active' : ''}`}
                        onClick={() => setDotType(item.id)}
                      >
                        {item.label}
                      </div>
                    ))}
                  </div>

                  <Form.Label className="fw-bold text-dark mb-2 mt-3">모서리 눈 외곽 (Corner Squares)</Form.Label>
                  <div className="qr-shape-grid">
                    {[
                      { id: 'extra-rounded', label: '둥근 모서리' },
                      { id: 'dot', label: '원형' },
                      { id: 'square', label: '클래식 사각' }
                    ].map(item => (
                      <div
                        key={item.id}
                        className={`qr-shape-item ${cornerSquareType === item.id ? 'active' : ''}`}
                        onClick={() => setCornerSquareType(item.id)}
                      >
                        {item.label}
                      </div>
                    ))}
                  </div>

                  <Form.Label className="fw-bold text-dark mb-2 mt-3">모서리 눈 내부 (Corner Center Dot)</Form.Label>
                  <div className="qr-shape-grid">
                    {[
                      { id: 'dot', label: '원형 도트' },
                      { id: 'square', label: '사각 점' }
                    ].map(item => (
                      <div
                        key={item.id}
                        className={`qr-shape-item ${cornerDotType === item.id ? 'active' : ''}`}
                        onClick={() => setCornerDotType(item.id)}
                      >
                        {item.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 탭 4: 색상 & 그라데이션 */}
              {activeTab === 'color' && (
                <div>
                  <Form.Label className="fw-bold text-dark mb-2">인기 추천 테마 프리셋</Form.Label>
                  <div className="qr-theme-preset-container">
                    {THEME_PRESETS.map(preset => (
                      <button
                        key={preset.id}
                        type="button"
                        className="qr-theme-preset-btn"
                        onClick={() => handleApplyTheme(preset)}
                      >
                        <span
                          className="qr-theme-preset-circle"
                          style={{
                            background: preset.isGradient
                              ? `linear-gradient(135deg, ${preset.dotColor1}, ${preset.dotColor2})`
                              : preset.dotColor1
                          }}
                        />
                        <span>{preset.name}</span>
                      </button>
                    ))}
                  </div>

                  <div className="d-flex align-items-center justify-content-between mb-3 pt-2 border-top">
                    <span className="fw-bold text-dark">그라데이션 색상 적용</span>
                    <Form.Check
                      type="switch"
                      id="gradient-switch"
                      checked={isGradient}
                      onChange={(e) => setIsGradient(e.target.checked)}
                    />
                  </div>

                  <Row className="g-3 mb-3">
                    <Col sm={6}>
                      <Form.Label className="small fw-bold">QR 도트 기본 색상</Form.Label>
                      <div className="d-flex align-items-center gap-2">
                        <Form.Control
                          type="color"
                          value={dotColor1}
                          onChange={(e) => setDotColor1(e.target.value)}
                          style={{ width: '48px', height: '38px', padding: '2px' }}
                        />
                        <Form.Control
                          type="text"
                          value={dotColor1}
                          onChange={(e) => setDotColor1(e.target.value)}
                        />
                      </div>
                    </Col>

                    {isGradient && (
                      <Col sm={6}>
                        <Form.Label className="small fw-bold">그라데이션 종료 색상</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                          <Form.Control
                            type="color"
                            value={dotColor2}
                            onChange={(e) => setDotColor2(e.target.value)}
                            style={{ width: '48px', height: '38px', padding: '2px' }}
                          />
                          <Form.Control
                            type="text"
                            value={dotColor2}
                            onChange={(e) => setDotColor2(e.target.value)}
                          />
                        </div>
                      </Col>
                    )}
                  </Row>

                  <Row className="g-3">
                    <Col sm={6}>
                      <Form.Label className="small fw-bold">모서리 눈 외곽 색상</Form.Label>
                      <div className="d-flex align-items-center gap-2">
                        <Form.Control
                          type="color"
                          value={cornerSquareColor}
                          onChange={(e) => setCornerSquareColor(e.target.value)}
                          style={{ width: '48px', height: '38px', padding: '2px' }}
                        />
                        <Form.Control
                          type="text"
                          value={cornerSquareColor}
                          onChange={(e) => setCornerSquareColor(e.target.value)}
                        />
                      </div>
                    </Col>
                    <Col sm={6}>
                      <Form.Label className="small fw-bold">QR 코드 배경색</Form.Label>
                      <div className="d-flex align-items-center gap-2">
                        <Form.Control
                          type="color"
                          value={bgColor}
                          onChange={(e) => setBgColor(e.target.value)}
                          style={{ width: '48px', height: '38px', padding: '2px' }}
                        />
                        <Form.Control
                          type="text"
                          value={bgColor}
                          onChange={(e) => setBgColor(e.target.value)}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {/* 탭 5: 페페 로고 & 이미지 */}
              {activeTab === 'pepe' && (
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <span className="fw-bold text-dark">중앙 이미지 삽입 활성화</span>
                    <Form.Check
                      type="switch"
                      id="logo-switch"
                      checked={enableLogo}
                      onChange={(e) => setEnableLogo(e.target.checked)}
                    />
                  </div>

                  {enableLogo && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">
                          페페 로고 크기 조절: {Math.round(logoSize * 100)}%
                        </Form.Label>
                        <Form.Range
                          min={0.15}
                          max={0.32}
                          step={0.01}
                          value={logoSize}
                          onChange={(e) => setLogoSize(parseFloat(e.target.value))}
                        />
                        <Form.Text className="text-muted">
                          권장 크기: 20% ~ 26% (스캔 인식이 가장 빠르고 정확합니다)
                        </Form.Text>
                      </Form.Group>

                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <span className="small fw-bold">로고 뒤 QR 도트 숨김 (Excavation)</span>
                        <Form.Check
                          type="switch"
                          id="hide-dots-switch"
                          checked={hideBackgroundDots}
                          onChange={(e) => setHideBackgroundDots(e.target.checked)}
                        />
                      </div>

                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">로고 여백 마진: {logoMargin}px</Form.Label>
                        <Form.Range
                          min={0}
                          max={12}
                          step={1}
                          value={logoMargin}
                          onChange={(e) => setLogoMargin(parseInt(e.target.value, 10))}
                        />
                      </Form.Group>

                      <div className="d-flex gap-2 mt-4 pt-3 border-top">
                        <Button
                          variant="success"
                          className="d-flex align-items-center gap-2 fw-bold"
                          onClick={handleResetToPepe}
                        >
                          <FaFrog /> 페페 이미지로 복원
                        </Button>
                        <Button
                          variant="outline-secondary"
                          className="d-flex align-items-center gap-2"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <BsUpload /> 다른 로고 올려보기
                        </Button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleCustomLogoUpload}
                        />
                      </div>
                    </motion.div>
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default DevQRCode;
